import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Collapse, message, Select, Space, Spin, Tag, Typography } from 'antd';
import { ThunderboltOutlined, MessageOutlined } from '@ant-design/icons';
import type { HermesItemResult, HermesHistoryEntry, HermesEstimationMode, HermesScenario } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { estimateItem, getItemHermesResult, getItemHermesHistory, applyHermesScenario } from './api.ts';
import { HermesResultView } from './HermesResultView.tsx';
import { HermesDialogBlock } from './HermesDialogBlock.tsx';
import { useHermesSessions } from './HermesSessionsContext.tsx';
import { HERMES_FINALIZED_EVENT, type HermesFinalizedDetail } from './HermesSessionDock.tsx';
import { useJobs } from '../jobs/JobsContext.tsx';

const SCENARIO_RU: Record<HermesScenario, string> = {
  realistic: 'реалистичный',
  optimistic: 'оптимистичный',
  pessimistic: 'пессимистичный',
};

interface EstimateRecordDrawerProps {
  spaceId: string;
  itemId: string;
  myRole?: string;
  /** Ключ записи (ITEM-238) — для заголовка интерактивной сессии. */
  itemKey?: string;
  /** Название записи (поле с признаком «название») — заголовок сессии. */
  itemTitle?: string;
  /** Вызывается после запуска интерактивной сессии — чтобы закрыть карточку
   * (она — полноэкранная модалка и иначе перекрыла бы панель сессий). */
  onLaunchInteractive?: () => void;
}

const POLL_MS = 3000;

const MODE_OPTIONS: Array<{ value: HermesEstimationMode; label: string }> = [
  { value: 'auto', label: 'Авто' },
  { value: 'high_level', label: 'Высокоуровневая' },
  { value: 'detailed', label: 'Детальная' },
];

// Откуда пришла оценка — чтобы в истории было видно, кнопка это, диалог или агент.
const SOURCE_LABEL: Record<string, { text: string; color: string }> = {
  api: { text: 'кнопка «Оценить»', color: 'default' },
  embed: { text: 'диалог с Гермесом', color: 'purple' },
  mcp: { text: 'агент', color: 'cyan' },
};

const STATUS_LABEL: Record<string, { text: string; color: string }> = {
  pending: { text: 'В очереди', color: 'default' },
  running: { text: 'Считается', color: 'processing' },
  completed: { text: 'Готово', color: 'success' },
  failed: { text: 'Ошибка', color: 'error' },
  timeout: { text: 'Таймаут', color: 'warning' },
  error: { text: 'Ошибка', color: 'error' },
};

export function EstimateRecordDrawer({ spaceId, itemId, myRole, itemKey, itemTitle, onLaunchInteractive }: EstimateRecordDrawerProps): JSX.Element {
  const canEstimate = myRole === 'admin' || myRole === 'member';
  const { openSession: openHermesSession } = useHermesSessions();
  const { registerJob } = useJobs();
  const [data, setData] = useState<HermesItemResult | null>(null);
  const [history, setHistory] = useState<HermesHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<HermesEstimationMode>('auto');
  const [applyingScenario, setApplyingScenario] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const [res, hist] = await Promise.all([
        getItemHermesResult(spaceId, itemId),
        getItemHermesHistory(spaceId, itemId).catch(() => [] as HermesHistoryEntry[]),
      ]);
      setData(res);
      setHistory(hist);
      if (res.status === 'running' || res.status === 'pending') setRunning(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить оценку');
    } finally {
      setLoading(false);
    }
  }, [spaceId, itemId]);

  useEffect(() => {
    void load();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  // Оценка считается в фоне (poller на бэке), поэтому опрашиваем статус самой
  // записи до терминального, а не job (job завершается сразу после старта).
  const TERMINAL = ['completed', 'failed', 'timeout', 'error'];
  const pollItem = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await getItemHermesResult(spaceId, itemId);
        if (res.status && TERMINAL.includes(res.status)) {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setRunning(false);
          if (res.status !== 'completed') {
            setError(
              res.status === 'timeout'
                ? 'Оценка не завершилась за отведённое время'
                : 'Оценка завершилась с ошибкой',
            );
          }
          await load();
        }
      } catch {
        /* транзиентная ошибка опроса — следующий тик повторит */
      }
    }, POLL_MS);
  }, [spaceId, itemId, load]);

  const handleEstimate = async () => {
    setError(null);
    setRunning(true);
    try {
      const { jobId } = await estimateItem(spaceId, itemId, mode);
      registerJob(jobId, `Оценка Hermes${itemKey ? ` · ${itemKey}` : ' · одна запись'}`);
      pollItem();
    } catch (err) {
      setRunning(false);
      setError(err instanceof ApiError ? err.message : 'Не удалось запустить оценку');
    }
  };

  const handleApplyScenario = async (scenario: HermesScenario) => {
    setApplyingScenario(true);
    try {
      const res = await applyHermesScenario(spaceId, itemId, scenario);
      // Тот же механизм, что и у интерактивной финализации: грид и открытая
      // карточка перечитывают запись, чтобы показать записанные значения.
      const detail: HermesFinalizedDetail = { itemId, spaceId, status: 'completed' };
      window.dispatchEvent(new CustomEvent(HERMES_FINALIZED_EVENT, { detail }));
      void message.success(
        `Записан ${SCENARIO_RU[scenario]} сценарий${res.writtenFields.length ? ` · полей: ${res.writtenFields.length}` : ''}`,
      );
    } catch (err) {
      void message.error(err instanceof ApiError ? err.message : 'Не удалось записать сценарий в поля');
    } finally {
      setApplyingScenario(false);
    }
  };

  if (loading) return <Spin />;

  // Что уходило в Гермес — сохраняется вместе с оценкой, чтобы её можно было
  // объяснить и повторить. Заполняет тот, кто запускал (агент — через MCP).
  const contextBlock = (h: HermesHistoryEntry): JSX.Element | null => {
    const c = h.context;
    if (!c) return null;
    return (
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: 'ctx',
            label: <Typography.Text type="secondary">Что уходило в Гермес</Typography.Text>,
            children: (
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {c.taskDescription && (
                  <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
                    {c.taskDescription}
                  </Typography.Paragraph>
                )}
                {c.answers?.length ? (
                  <div>
                    <Typography.Text strong>Ответы на вопросы</Typography.Text>
                    {c.answers.map((a, i) => (
                      <Typography.Paragraph key={i} style={{ marginBottom: 4 }}>
                        <Typography.Text type="secondary">{a.question}</Typography.Text>
                        <br />
                        {a.answer}
                      </Typography.Paragraph>
                    ))}
                  </div>
                ) : null}
                {c.sources?.length ? (
                  <div>
                    <Typography.Text strong>Источники</Typography.Text>
                    <ul style={{ margin: '4px 0 0 18px' }}>
                      {c.sources.map((src, i) => (
                        <li key={i}>{src}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {c.note && <Typography.Text italic>{c.note}</Typography.Text>}
                {h.skippedManualFields.length > 0 && (
                  <Typography.Text type="warning">
                    Не перезаписаны (правили вручную): {h.skippedManualFields.join(', ')}
                  </Typography.Text>
                )}
              </Space>
            ),
          },
        ]}
      />
    );
  };

  const latest = history[0] ?? null;

  const status = data?.status ?? null;
  const statusMeta = status ? STATUS_LABEL[status] : null;
  const result = data?.result ?? null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space align="center">
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={running}
          disabled={!canEstimate}
          onClick={handleEstimate}
        >
          {result ? 'Переоценить' : 'Оценить трудоёмкость'}
        </Button>
        {canEstimate && (
          <Button
            icon={<MessageOutlined />}
            onClick={() => {
              openHermesSession({ spaceId, itemId, itemKey, title: itemTitle || itemKey || 'Запись', mode });
              onLaunchInteractive?.();
            }}
          >
            Интерактивная
          </Button>
        )}
        {canEstimate && (
          <Select
            size="small"
            value={mode}
            onChange={setMode}
            disabled={running}
            options={MODE_OPTIONS}
            style={{ minWidth: 150 }}
            title="Режим оценки"
          />
        )}
        {statusMeta && <Tag color={statusMeta.color}>{statusMeta.text}</Tag>}
        {running && <Typography.Text type="secondary">оценка идёт в фоне…</Typography.Text>}
      </Space>

      {!canEstimate && (
        <Typography.Text type="secondary">Запуск оценки доступен участникам и администраторам.</Typography.Text>
      )}

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}

      {latest && latest.skippedManualFields.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`Поля не перезаписаны, их правили вручную: ${latest.skippedManualFields.join(', ')}`}
          description="Чтобы записать значения оценки поверх, выберите сценарий кнопкой «Записать в поля»."
        />
      )}

      {result && (
        <HermesResultView
          result={result}
          onApplyScenario={canEstimate ? handleApplyScenario : undefined}
          applyingScenario={applyingScenario}
        />
      )}

      {latest && contextBlock(latest)}
      {latest && <HermesDialogBlock spaceId={spaceId} itemId={itemId} entry={latest} />}

      {history.length > 1 && (
        <Collapse
          ghost
          items={[
            {
              key: 'history',
              label: <Typography.Text strong>История оценок ({history.length})</Typography.Text>,
              children: (
                <Collapse
                  size="small"
                  items={history.map((h) => {
                    const t = h.result?.estimate?.total;
                    const unit = h.result?.estimate?.unit ?? '';
                    const src = h.source ? SOURCE_LABEL[h.source] : null;
                    return {
                      key: h.id,
                      label: (
                        <Space>
                          <Typography.Text>{new Date(h.requestedAt).toLocaleString('ru-RU')}</Typography.Text>
                          {t?.realistic != null && (
                            <Tag>{Math.round(t.realistic)} {unit}</Tag>
                          )}
                          {h.result?.task_class && <Tag color="blue">{h.result.task_class}</Tag>}
                          {src && <Tag color={src.color}>{src.text}</Tag>}
                          {h.requestedBy && (
                            <Typography.Text type="secondary">{h.requestedBy}</Typography.Text>
                          )}
                        </Space>
                      ),
                      children: h.result ? (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <HermesResultView result={h.result} />
                          {contextBlock(h)}
                          <HermesDialogBlock spaceId={spaceId} itemId={itemId} entry={h} />
                        </Space>
                      ) : (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Typography.Text type="secondary">Нет данных результата</Typography.Text>
                          <HermesDialogBlock spaceId={spaceId} itemId={itemId} entry={h} />
                        </Space>
                      ),
                    };
                  })}
                />
              ),
            },
          ]}
        />
      )}

      {!result && !running && status !== 'completed' && (
        <Typography.Text type="secondary">Оценка ещё не запускалась для этой записи.</Typography.Text>
      )}
    </Space>
  );
}
